import type { Temporal } from '@js-temporal/polyfill'
import { playerGlassStateHeadline } from '@lucimark/shared/helpers/playerGlassState'
import type { AspectRatio } from '@lucimark/shared/database/custom-types/aspect-ratio'
import type { IPlaybackScheduleRoot } from '@lucimark/shared/models/player-registration-payload'
import type { TGeoLatLng } from '@lucimark/shared/models/playlist-assignment-geo-filter'
import { playbackScheduleHasFrameGeoFilter } from '@lucimark/shared/helpers/frameGeoFilter'
import { resolvePlaybackSegments } from '@lucimark/shared/helpers/resolvePlaybackSegments'

export type TPlayerIdleWaitingFeedbackCause =
  | 'load_error'
  | 'video_skipped_low_capacity'
  | 'aspect_ratio_filtered'
  | 'geo_filter_unsatisfied'
  | 'no_active_frames'
  | 'waiting_schedule'

export interface IPlayerIdleWaitingFeedback {
  cause: TPlayerIdleWaitingFeedbackCause
  /** Short status line under the idle logo. */
  headline: string
  /** Optional operator-facing explanation (on-device). */
  detail: string | null
  tone: 'info' | 'warning' | 'error'
}

export interface IResolvePlayerIdleWaitingFeedbackInput {
  campaignLoadError: string | null
  campaign: IPlaybackScheduleRoot | null
  queueLength: number
  /** True when low-capacity video skip is active for this device/tenant. */
  excludeFramesWithVideo: boolean
  allowedAspectRatios: ReadonlySet<AspectRatio> | null
  portalBookViewportAspect?: AspectRatio | null
  operatorMasterPlaylistUrn?: string | null
  referenceInstant: Temporal.Instant
  /**
   * Resolved player point for assignment geofilters (GPS → venue → stamped fallback).
   * Omit only when the caller cannot resolve; missing point fail-closes geo-filtered rows.
   */
  geoPoint?: TGeoLatLng | null
}

/**
 * Explains why a registered player is on the idle waiting screen with an empty playback queue.
 * Uses the same segment resolver as materialization so copy matches real filter behaviour.
 */
export function resolvePlayerIdleWaitingFeedback(
  input: IResolvePlayerIdleWaitingFeedbackInput
): IPlayerIdleWaitingFeedback {
  if (input.campaignLoadError != null && input.campaignLoadError !== '' && input.queueLength < 1) {
    return {
      cause: 'load_error',
      headline: 'Erro ao carregar programação',
      detail: input.campaignLoadError,
      tone: 'error'
    }
  }

  if (input.campaign == null) {
    return {
      cause: 'waiting_schedule',
      headline: playerGlassStateHeadline('waiting_for_publication', 'pt'),
      detail: null,
      tone: 'info'
    }
  }

  if (input.queueLength >= 1) {
    return {
      cause: 'waiting_schedule',
      headline: 'Aguardando programação',
      detail: null,
      tone: 'info'
    }
  }

  const geoPoint = input.geoPoint ?? null
  const baseOpts = {
    portalBookViewportAspect: input.portalBookViewportAspect ?? null,
    operatorMasterPlaylistUrn: input.operatorMasterPlaylistUrn ?? null,
    geoPoint
  }

  const withCurrentFilters = resolvePlaybackSegments(input.campaign, input.referenceInstant, {
    ...baseOpts,
    allowedAspectRatios: input.allowedAspectRatios,
    excludeFramesWithVideo: input.excludeFramesWithVideo
  })
  if (withCurrentFilters.length > 0) {
    // Queue empty while resolver still sees frames — usually a brief load/hold race.
    return {
      cause: 'waiting_schedule',
      headline: 'A preparar conteúdo',
      detail: 'A programação chegou; a fila de reprodução ainda está a ser montada.',
      tone: 'info'
    }
  }

  const withoutVideoExclude = input.excludeFramesWithVideo
    ? resolvePlaybackSegments(input.campaign, input.referenceInstant, {
        ...baseOpts,
        allowedAspectRatios: input.allowedAspectRatios,
        excludeFramesWithVideo: false
      })
    : withCurrentFilters

  if (input.excludeFramesWithVideo && withoutVideoExclude.length > 0) {
    return {
      cause: 'video_skipped_low_capacity',
      headline: 'Vídeo omitido neste dispositivo',
      detail:
        'Capacidade baixa: a programação tem vídeo, mas este ecrã não o reproduz. Inclua imagens na playlist ou active «Permitir vídeo em dispositivos de baixa capacidade» nas definições do Studio.',
      tone: 'warning'
    }
  }

  const aspectRestricted = input.allowedAspectRatios != null && input.allowedAspectRatios.size > 0
  const withoutAspectFilter = aspectRestricted
    ? resolvePlaybackSegments(input.campaign, input.referenceInstant, {
        ...baseOpts,
        allowedAspectRatios: null,
        excludeFramesWithVideo: input.excludeFramesWithVideo
      })
    : withCurrentFilters

  if (aspectRestricted && withoutAspectFilter.length > 0) {
    return {
      cause: 'aspect_ratio_filtered',
      headline: 'Formato não compatível',
      detail:
        'Há conteúdo na campanha, mas nenhum criativo corresponde à proporção deste ecrã.',
      tone: 'warning'
    }
  }

  const withoutBothFilters = resolvePlaybackSegments(input.campaign, input.referenceInstant, {
    ...baseOpts,
    allowedAspectRatios: null,
    excludeFramesWithVideo: false
  })

  if (withoutBothFilters.length > 0) {
    return {
      cause: 'no_active_frames',
      headline: 'Sem conteúdo reproduzível',
      detail:
        'A campanha está atribuída, mas os filtros deste ecrã (vídeo / formato) removeram todos os itens activos.',
      tone: 'warning'
    }
  }

  const playlistCount = input.campaign.playlists?.length ?? 0
  if (playlistCount === 0) {
    return {
      cause: 'no_active_frames',
      headline: 'Campanha sem playlist',
      detail: 'A campanha está atribuída, mas não tem playlists com conteúdo para este ecrã.',
      tone: 'warning'
    }
  }

  const hasGeoFilteredRows = (input.campaign.playlists ?? []).some(pl => pl.geoFilter != null)
    || playbackScheduleHasFrameGeoFilter(input.campaign)
  if (hasGeoFilteredRows) {
    const ignoringGeo = resolvePlaybackSegments(input.campaign, input.referenceInstant, {
      portalBookViewportAspect: input.portalBookViewportAspect ?? null,
      operatorMasterPlaylistUrn: input.operatorMasterPlaylistUrn ?? null,
      allowedAspectRatios: null,
      excludeFramesWithVideo: false,
      skipGeoFilter: true
    })
    if (ignoringGeo.length > 0) {
      return {
        cause: 'geo_filter_unsatisfied',
        headline: geoPoint == null ? 'Aguardando localização GPS' : 'Fora da área geográfica',
        detail: geoPoint == null
          ? 'Há itens com filtro geográfico, mas ainda não há posição GPS/venue para avaliar.'
          : 'A posição actual não está dentro de nenhum círculo de filtro geográfico dos itens activos.',
        tone: 'warning'
      }
    }
  }

  return {
    cause: 'no_active_frames',
    headline: 'Sem conteúdo activo agora',
    detail:
      'A campanha está atribuída, mas não há itens a reproduzir neste momento (fora do horário ou playlist vazia).',
    tone: 'info'
  }
}
